/**
 * 構造化ログシステム
 * 開発・デバッグ効率向上のためのエラーログ機能
 */

import type { LogEntry } from '../types';

// ログレベルの定義
const LOG_LEVELS = {
  ERROR: 0,
  WARN: 1,
  INFO: 2,
  DEBUG: 3
} as const;

type LogLevel = (typeof LOG_LEVELS)[keyof typeof LOG_LEVELS];

// ログレベル名
const LOG_LEVEL_NAMES: Record<LogLevel, string> = {
  0: 'ERROR',
  1: 'WARN',
  2: 'INFO',
  3: 'DEBUG'
};

// 環境に応じたログレベル設定
const getCurrentLogLevel = (): LogLevel => {
  if (process.env.NODE_ENV === 'production') {
    return LOG_LEVELS.ERROR;
  }
  if (process.env.NODE_ENV === 'test') {
    return LOG_LEVELS.WARN;
  }
  return LOG_LEVELS.DEBUG; // development
};

// エラー頻度追跡用のカウンタ
const errorCounter = new Map<string, number>();

interface LogEntryInternal extends LogEntry {
  sessionId: string;
  userAgent: string;
  url: string;
  [key: string]: any;
}

/**
 * 構造化ログエントリを作成
 * @param level - ログレベル
 * @param message - メッセージ
 * @param metadata - 追加のメタデータ
 * @returns 構造化ログオブジェクト
 */
const createLogEntry = (
  level: string,
  message: string,
  metadata: Record<string, any> = {}
): LogEntryInternal => {
  const timestamp = new Date().toISOString();
  const sessionId = getSessionId();

  return {
    timestamp,
    level: level as LogEntry['level'],
    message,
    sessionId,
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'Unknown',
    url: typeof window !== 'undefined' ? window.location.href : 'Unknown',
    ...metadata
  };
};

/**
 * セッションIDを取得または生成
 * @returns セッションID
 */
const getSessionId = (): string => {
  if (typeof window !== 'undefined') {
    let sessionId = sessionStorage.getItem('niconico_session_id');
    if (!sessionId) {
      sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      sessionStorage.setItem('niconico_session_id', sessionId);
    }
    return sessionId;
  }
  return 'server_session';
};

/**
 * 個人情報をマスキングする
 * @param data - マスキング対象のデータ
 * @returns マスキングされたデータ
 */
const sanitizeMetadata = (data: any): any => {
  if (!data) return data;

  // プリミティブ型の場合はそのまま返す
  if (typeof data !== 'object') return data;

  // 配列の場合は各要素を再帰的に処理
  if (Array.isArray(data)) {
    return data.map((item) => sanitizeMetadata(item));
  }

  // オブジェクトの場合は各プロパティを処理
  const sanitized: Record<string, any> = {};
  for (const [key, value] of Object.entries(data)) {
    const lowerKey = key.toLowerCase();

    // 個人情報に関連するキーをマスキング
    if (lowerKey.includes('phone') || lowerKey.includes('電話')) {
      sanitized[key] = maskPhoneNumber(value);
    } else if (
      lowerKey.includes('name') ||
      lowerKey.includes('名前') ||
      lowerKey.includes('氏名')
    ) {
      sanitized[key] = maskName(value);
    } else if (lowerKey.includes('email') || lowerKey.includes('メール')) {
      sanitized[key] = maskEmail(value);
    } else if (lowerKey.includes('address') || lowerKey.includes('住所')) {
      sanitized[key] = maskAddress(value);
    } else if (lowerKey.includes('birth') || lowerKey.includes('生年月日')) {
      sanitized[key] = maskBirthDate(value);
    } else if (typeof value === 'object') {
      sanitized[key] = sanitizeMetadata(value);
    } else {
      sanitized[key] = value;
    }
  }

  return sanitized;
};

/**
 * 電話番号をマスキング
 * @param phone - 電話番号
 * @returns マスキングされた電話番号
 */
const maskPhoneNumber = (phone: any): any => {
  if (!phone || typeof phone !== 'string') return phone;

  // 数字のみを抽出
  const digitsOnly = phone.replace(/\D/g, '');

  // 電話番号が短すぎる場合はそのまま返す
  if (digitsOnly.length <= 5) return phone;

  // 最初の3桁と最後の2桁を残して中間をマスキング
  const firstThree = digitsOnly.substring(0, 3);
  const lastTwo = digitsOnly.substring(digitsOnly.length - 2);
  const middleLength = digitsOnly.length - 5;
  const masked = firstThree + '*'.repeat(middleLength) + lastTwo;

  // 元の形式に数字以外の文字を復元
  let result = '';
  let maskedIndex = 0;

  for (let i = 0; i < phone.length; i++) {
    if (/\d/.test(phone[i])) {
      result += masked[maskedIndex++];
    } else {
      result += phone[i];
    }
  }

  return result;
};

/**
 * 名前をマスキング
 * @param name - 名前
 * @returns マスキングされた名前
 */
const maskName = (name: any): any => {
  if (!name || typeof name !== 'string') return name;
  // 最初の1文字を残してマスキング
  return name.charAt(0) + '*'.repeat(Math.min(name.length - 1, 5));
};

/**
 * メールアドレスをマスキング
 * @param email - メールアドレス
 * @returns マスキングされたメールアドレス
 */
const maskEmail = (email: any): any => {
  if (!email || typeof email !== 'string') return email;
  const [localPart, domain] = email.split('@');
  if (!domain) return email;

  const maskedLocal = localPart.charAt(0) + '***';
  return `${maskedLocal}@${domain}`;
};

/**
 * 住所をマスキング
 * @param address - 住所
 * @returns マスキングされた住所
 */
const maskAddress = (address: any): any => {
  if (!address || typeof address !== 'string') return address;
  // 最初の10文字を残してマスキング
  return address.substring(0, 10) + '***';
};

/**
 * 生年月日をマスキング
 * @param birthDate - 生年月日
 * @returns マスキングされた生年月日
 */
const maskBirthDate = (birthDate: any): any => {
  if (!birthDate || typeof birthDate !== 'string') return birthDate;

  // 日付フォーマットを検証（YYYY-MM-DD形式）
  const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
  if (!dateRegex.test(birthDate)) {
    // 無効なフォーマットの場合は文字列の最初の部分のみを表示
    if (birthDate.length > 4) {
      return birthDate.substring(0, 4) + '****';
    }
    return birthDate;
  }

  // 日付の妥当性をチェック
  const [year, month, day] = birthDate.split('-').map(Number);
  const date = new Date(year, month - 1, day);

  // 有効な日付かチェック（例：2月31日などの無効な日付を検出）
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
    // 無効な日付の場合は年だけを表示
    return `${year}-**-**`;
  }

  // 有効な日付の場合は年だけを表示
  return `${year}-**-**`;
};

/**
 * エラー頻度を追跡
 * @param errorKey - エラーの識別キー
 */
const trackErrorFrequency = (errorKey: string): void => {
  const count = errorCounter.get(errorKey) || 0;
  errorCounter.set(errorKey, count + 1);

  // 開発環境では頻発エラーを警告
  if (process.env.NODE_ENV === 'development' && count > 0 && count % 5 === 0) {
    console.warn(`[Logger] エラーが頻発しています: ${errorKey} (${count + 1}回)`);
  }
};

/**
 * ログを出力する基本関数
 * @param level - ログレベル
 * @param message - メッセージ
 * @param metadata - 追加のメタデータ
 */
const logWithLevel = (
  level: LogLevel,
  message: string,
  metadata: Record<string, any> = {}
): void => {
  const currentLevel = getCurrentLogLevel();

  // ログレベルフィルタリング
  if (level > currentLevel) {
    return;
  }

  const sanitizedMetadata = sanitizeMetadata(metadata);
  const logEntry = createLogEntry(LOG_LEVEL_NAMES[level], message, sanitizedMetadata);

  // コンソール出力（環境に応じてスタイリング）
  if (typeof console !== 'undefined') {
    const consoleMethod =
      level === LOG_LEVELS.ERROR
        ? 'error'
        : level === LOG_LEVELS.WARN
          ? 'warn'
          : level === LOG_LEVELS.INFO
            ? 'info'
            : 'log';

    if (process.env.NODE_ENV === 'development') {
      // 開発環境では詳細な情報を表示
      console.group(`[${logEntry.level}] ${logEntry.message}`);
      console.log('📅 時刻:', logEntry.timestamp);
      console.log('🔗 セッション:', logEntry.sessionId);
      console.log('📍 URL:', logEntry.url);
      if (Object.keys(sanitizedMetadata).length > 0) {
        console.log('📊 詳細:', sanitizedMetadata);
      }
      console.groupEnd();
    } else {
      // 本番環境ではシンプルな出力
      console[consoleMethod](`[${logEntry.level}] ${logEntry.message}`, sanitizedMetadata);
    }
  }

  // 将来的なログ保存・送信の基盤
  if (typeof window !== 'undefined' && (window as any).logCollector) {
    (window as any).logCollector.send(logEntry);
  }
};

/**
 * ログ出力関数群
 */
export const logger = {
  /**
   * エラーログ
   * @param message - エラーメッセージ
   * @param metadata - 追加情報
   */
  error: (message: string, metadata: Record<string, any> = {}): void => {
    const errorKey = `${message}_${metadata.context || 'unknown'}`;
    trackErrorFrequency(errorKey);

    logWithLevel(LOG_LEVELS.ERROR, message, {
      ...metadata,
      errorFrequency: errorCounter.get(errorKey)
    });
  },

  /**
   * 警告ログ
   * @param message - 警告メッセージ
   * @param metadata - 追加情報
   */
  warn: (message: string, metadata: Record<string, any> = {}): void => {
    logWithLevel(LOG_LEVELS.WARN, message, metadata);
  },

  /**
   * 情報ログ
   * @param message - 情報メッセージ
   * @param metadata - 追加情報
   */
  info: (message: string, metadata: Record<string, any> = {}): void => {
    logWithLevel(LOG_LEVELS.INFO, message, metadata);
  },

  /**
   * デバッグログ
   * @param message - デバッグメッセージ
   * @param metadata - 追加情報
   */
  debug: (message: string, metadata: Record<string, any> = {}): void => {
    logWithLevel(LOG_LEVELS.DEBUG, message, metadata);
  },

  /**
   * APIリクエストログ
   * @param method - HTTPメソッド
   * @param url - リクエストURL
   * @param options - 追加オプション
   */
  apiRequest: (method: string, url: string, options: Record<string, any> = {}): void => {
    logger.info(`API Request: ${method} ${url}`, {
      context: 'api_request',
      method,
      url,
      ...options
    });
  },

  /**
   * APIレスポンスログ
   * @param method - HTTPメソッド
   * @param url - リクエストURL
   * @param status - ステータスコード
   * @param duration - レスポンス時間（ミリ秒）
   * @param options - 追加オプション
   */
  apiResponse: (
    method: string,
    url: string,
    status: number,
    duration: number,
    options: Record<string, any> = {}
  ): void => {
    const level = status >= 400 ? 'error' : status >= 300 ? 'warn' : 'info';
    logger[level](`API Response: ${method} ${url} ${status} (${duration}ms)`, {
      context: 'api_response',
      method,
      url,
      status,
      duration,
      ...options
    });
  },

  /**
   * ユーザーアクションログ
   * @param action - アクション名
   * @param details - アクション詳細
   */
  userAction: (action: string, details: Record<string, any> = {}): void => {
    logger.info(`User Action: ${action}`, {
      context: 'user_action',
      action,
      ...details
    });
  },

  /**
   * パフォーマンスログ
   * @param operation - 操作名
   * @param duration - 処理時間（ミリ秒）
   * @param metadata - 追加のメタデータ
   */
  performance: (operation: string, duration: number, metadata: Record<string, any> = {}): void => {
    const level = duration > 3000 ? 'warn' : duration > 1000 ? 'info' : 'debug';
    logger[level](`Performance: ${operation} completed in ${duration}ms`, {
      context: 'performance',
      operation,
      duration,
      ...metadata
    });
  }
};

interface ErrorFrequencyStat {
  error: string;
  count: number;
}

/**
 * エラー頻度統計を取得
 * @returns エラー頻度の配列
 */
export const getErrorFrequencyStats = (): ErrorFrequencyStat[] => {
  return Array.from(errorCounter.entries())
    .map(([error, count]) => ({ error, count }))
    .sort((a, b) => b.count - a.count);
};

/**
 * エラー頻度統計をリセット
 */
export const resetErrorFrequencyStats = (): void => {
  errorCounter.clear();
};

/**
 * 現在のログレベルを取得
 * @returns ログレベル名
 */
export const getCurrentLogLevelName = (): string => {
  return LOG_LEVEL_NAMES[getCurrentLogLevel()];
};

interface ErrorReport {
  timestamp: string;
  sessionId: string;
  error: {
    name: string;
    message: string;
    stack?: string;
  };
  context: Record<string, any>;
  userAgent: string;
  url: string;
  errorFrequency: ErrorFrequencyStat[];
}

/**
 * エラーレポート機能の基盤
 * 将来的にサーバーへのエラー送信などに利用
 */
export const errorReporter = {
  /**
   * エラーレポートを生成
   * @param error - エラーオブジェクト
   * @param context - エラーコンテキスト
   * @returns エラーレポート
   */
  generateReport: (error: Error, context: Record<string, any> = {}): ErrorReport => {
    return {
      timestamp: new Date().toISOString(),
      sessionId: getSessionId(),
      error: {
        name: error.name,
        message: error.message,
        stack: error.stack
      },
      context,
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'Unknown',
      url: typeof window !== 'undefined' ? window.location.href : 'Unknown',
      errorFrequency: getErrorFrequencyStats()
    };
  },

  /**
   * エラーレポートをローカルストレージに保存
   * @param report - エラーレポート
   */
  saveToLocalStorage: (report: ErrorReport): void => {
    if (typeof localStorage !== 'undefined') {
      try {
        const reports = JSON.parse(localStorage.getItem('niconico_error_reports') || '[]');
        reports.push(report);

        // 最新50件のみ保持
        if (reports.length > 50) {
          reports.splice(0, reports.length - 50);
        }

        localStorage.setItem('niconico_error_reports', JSON.stringify(reports));
      } catch (e) {
        console.warn('[Logger] エラーレポートの保存に失敗しました:', e);
      }
    }
  },

  /**
   * 保存されたエラーレポートを取得
   * @returns エラーレポートの配列
   */
  getSavedReports: (): ErrorReport[] => {
    if (typeof localStorage !== 'undefined') {
      try {
        return JSON.parse(localStorage.getItem('niconico_error_reports') || '[]');
      } catch (e) {
        console.warn('[Logger] エラーレポートの読み込みに失敗しました:', e);
        return [];
      }
    }
    return [];
  }
};

export default logger;
